import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import axiosInstance from "../utils/axiosInstance";

const API_BASE = process.env.REACT_APP_API_BASE;

const languageOptions = ["English", "German", "Dutch", "Korean", "Spanish", "French", "Italian", "Japanese", "Korean", "Arabic", "Chinese", "Russian"];
const levelOptions = ["A1", "A2", "B1", "B2", "C1", "C2"];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const { userId, token } = useContext(AuthContext);
  const [statusFilter, setStatusFilter] = useState("");

  const [filters, setFilters] = useState({
    currentLanguage: "",
    targetLanguage: "",
    level: "",
  });

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const [allUsersRes, currentUserRes] = await Promise.all([
          axios.get(`${API_BASE}/api/users`, {
            headers: { Authorization: token },
          }),
          axios.get(`${API_BASE}/api/users/${userId}`, {
            headers: { Authorization: token },
          }),
        ]);

        // Filter out the current user and update the PairLinged status
        const filteredUsers = allUsersRes.data
          .filter((u) => u._id !== userId)
          .map((user) => ({
            ...user,
            isPairLinged: user.isPairLinged || false, // Ensure the flag exists
          }));

        // Apply the status filter directly while fetching users
        let finalFilteredUsers = [...filteredUsers];

        if (statusFilter) {
          if (statusFilter === "available") {
            finalFilteredUsers = finalFilteredUsers.filter((u) => !u.isPairLinged);
          } else if (statusFilter === "pairlinged") {
            finalFilteredUsers = finalFilteredUsers.filter((u) => u.isPairLinged);
          }
        }

        setUsers(finalFilteredUsers);
        setFilteredUsers(finalFilteredUsers);
        setCurrentUser(currentUserRes.data);
      } catch (err) {
        console.error("Failed to fetch users", err);
      }
    };

    if (userId) fetchUsers();
  }, [userId, token, statusFilter]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    let filtered = [...users];
    if (filters.currentLanguage) {
      filtered = filtered.filter((u) => u.currentLanguage === filters.currentLanguage);
    }
    if (filters.targetLanguage) {
      filtered = filtered.filter((u) => u.targetLanguage === filters.targetLanguage);
    }
    if (filters.level) {
      filtered = filtered.filter((u) => u.level === filters.level);
    }
    setFilteredUsers(filtered);
  }, [filters, users]);

  const sendRequest = async (toUserId) => {
    try {
      await axiosInstance.post(
        `${API_BASE}/api/match/request`,
        { fromUserId: userId, toUserId },
        { headers: { Authorization: token } }
      );
      alert("Request sent!");
    } catch (err) {
      alert("Failed to send request.");
      console.error(err);
    }
  };

  const removeMatch = async (matchId) => {
    try {
      await axiosInstance.post(
        `${API_BASE}/api/users/remove-match`,
        { userId, matchId },
        { headers: { Authorization: token } }
      );
      const updated = await axios.get(`${API_BASE}/api/users/${userId}`, {
        headers: { Authorization: token },
      });
      setCurrentUser(updated.data);
    } catch (err) {
      console.error("Failed to remove match", err);
      alert("Failed to remove match.");
    }
  };

  const handleSendRequest = (u) => {
    if (u.isPairLinged) {
      toast.error("User is already PairLinged");
      return;
    }
    sendRequest(u._id);
  };

  const hasMatchedOrRequested = (u) => {
    if (!currentUser) return false;
    const matchIds = currentUser.matches.map((m) => m._id);
    const sentIds = currentUser.requestsSent.map((r) => r._id);
    return matchIds.includes(u._id) || sentIds.includes(u._id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-indigo-200 px-4 py-6">
      <Helmet>
        <title>Users - PairLingo</title>
        <meta name="description" content="Speak with perfect language partner on PairLingo." />
        <meta name="keywords" content="language learning, pairlingo, chat, match, speaking chat, german language partner chat, language speaking practice" />
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold mb-6 text-indigo-700">Explore Language Learners</h1>

        {/* Filter Section */}
        <div className="mb-4 p-4 bg-white rounded shadow-md flex gap-4 flex-wrap">
          <select
            name="currentLanguage"
            value={filters.currentLanguage}
            onChange={handleFilterChange}
            className="w-full sm:w-auto px-3 py-2 border rounded bg-white"
          >
            <option value="">Speaks</option>
            {languageOptions.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>

          <select
            name="targetLanguage"
            value={filters.targetLanguage}
            onChange={handleFilterChange}
            className="w-full sm:w-auto px-3 py-2 border rounded bg-white"
          >
            <option value="">Learning</option>
            {languageOptions.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>

          <select
            name="level"
            value={filters.level}
            onChange={handleFilterChange}
            className="w-full sm:w-auto px-3 py-2 border rounded bg-white"
          >
            <option value="">Level</option>
            {levelOptions.map((lvl) => (
              <option key={lvl} value={lvl}>
                {lvl}
              </option>
            ))}
          </select>

          <select onChange={handleStatusChange} value={statusFilter} className="px-3 py-2 border rounded bg-white">
            <option value="">Status</option>
            <option value="available">Available</option>
            <option value="pairlinged">PairLinged</option>
          </select>
        </div>

        <div className="space-y-4">
          {filteredUsers.map((u) => (
            <div
              key={u._id}
              className="w-full min-h-[160px] bg-white rounded-xl shadow-md p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center hover:shadow-lg transition-all"
            >
              <div className="flex items-start sm:items-center gap-4 w-full sm:w-3/4">
                <img
                  src={`https://ui-avatars.com/api/?name=${u.username}&background=random`}
                  alt={u.username}
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-indigo-800">{u.username}</h2>
                  <p className="text-sm text-gray-600">
                    Speaks: <span className="font-medium">{u.currentLanguage}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Learning: <span className="font-medium">{u.targetLanguage}</span> • Level:{" "}
                    <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">
                      {u.level}
                    </span>
                  </p>
                  <p className="text-sm italic text-gray-500 mt-1 line-clamp-2 overflow-hidden">
                    "{u.bio}"
                  </p>
                </div>
              </div>
              <div className="sm:ml-auto mt-4 sm:mt-0">
                {hasMatchedOrRequested(u) ? (
                  <button
                    onClick={() => removeMatch(u._id)}
                    className="bg-red-100 text-red-600 px-4 py-2 rounded text-sm hover:bg-red-200"
                  >
                    ❌ Remove
                  </button>
                ) : u.isPairLinged ? (
                  <button
                    className="bg-gray-300 text-gray-600 px-4 py-2 rounded text-sm cursor-not-allowed"
                    onClick={() => toast.error("User is currently occupied (PairLinged).")}
                  >
                    🚫 PairLinged
                  </button>
                ) : (
                  <button
                    onClick={() => sendRequest(u._id)}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-500"
                  >
                    🤝 Send Match Request
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Users;